import { Collapse } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AttachmentFiles from '@/pages/announcement-management/view/componnents/AttachmentFiles';
import { AttachmentFile } from '@/pages/announcement-management/view/models';
import { STATUS_NAME, StatusName } from '@pages/project-management/detail/constant';
import { renderTaskStatus } from '@pages/project-management/detail/utils/helper';

import { BaseButton } from '@/components/base-button/BaseButton';
import { ConfirmCancelEditing } from '@/components/confirm-popup';
import { CaretDownBold } from '@/components/icon-svg/IconSvg';
import AppTooltip from '@components/app-tooltip/AppTooltip';

import useAuthorization from '@/hooks/useAuthorization';

import { FORMAT_DATE_EN, FORMAT_TIME } from '@/utils/constants/AppConstants';
import { ATTACHMENT_FILE_STATUS } from '@/utils/constants/file';
import { Ii18n } from '@/utils/interfaces/i18n';
import { DataViewer } from '@utils/helpers/common';

import { ATTACHMENT_STATUS, DEFAULT_ACTIVE_COLLAPSE } from '../../constants';
import { IAttachmentTaskDetail, IDetailTaskData } from '../../models';
import FileUploadDetail from './FileUploadDetail';
import TaskRequiredDocumentsView from './TaskRequiredDocuments';

import '../index.scss';

interface DetailTaskExternalProps {
  detailData: IDetailTaskData;
  handleCancelUploadFileExternal: (e: any) => Promise<void>;
  handleUploadFileExternal: (e: any) => void;
}

const DetailTaskExternal = ({ detailData, handleCancelUploadFileExternal, handleUploadFileExternal }: DetailTaskExternalProps) => {
  const { t }: Ii18n = useTranslation();
  const form = useFormContext();
  const { isDirty, isSubmitting } = form.formState;
  const { taskRequireDocuments } = form.getValues();
  const { isExternalRole, isInternalRole } = useAuthorization();

  const isTaskDelay = useMemo(() => {
    if (detailData?.statusName === STATUS_NAME.CANCELLED || detailData?.statusName === STATUS_NAME.COMPLETED) {
      return false;
    }

    if (!detailData?.deadline) return false;

    const deadlineDate = dayjs(detailData.deadline);

    if (detailData.deadlineTime) {
      const time = dayjs(detailData.deadlineTime, 'HH:mm:ss');
      const fullDeadline = deadlineDate.hour(time.hour()).minute(time.minute()).second(time.second());

      return fullDeadline.isBefore(dayjs(), 'second');
    }

    return deadlineDate.isBefore(dayjs(), 'day');
  }, [detailData?.statusName, detailData?.deadline, detailData?.deadlineTime]);

  const documents = detailData?.taskRequireDocuments ?? [];

  const allFiles = documents.flatMap((doc) => doc.taskRequireDocumentAttachmentFiles ?? []);

  const hasRejectedFile = allFiles.some((file) => file.status === ATTACHMENT_STATUS.REJECT);

  const hasAnyProcessing = documents.some((doc) => {
    const files = doc.taskRequireDocumentAttachmentFiles ?? [];
    const hasFile = files.length > 0;
    const hasWaiting = files.some((f) => f.status === ATTACHMENT_STATUS.WAITING);

    return hasFile && (!doc.isConfirm || hasWaiting);
  });

  const hasDocWithoutFile = documents.some((doc) => {
    const files = doc.taskRequireDocumentAttachmentFiles ?? [];
    return files.length === 0;
  });

  const displayStatus: StatusName = (() => {
    if (detailData?.statusName === STATUS_NAME.COMPLETED || detailData?.statusName === STATUS_NAME.CANCELLED) {
      return detailData.statusName;
    }

    if (!isTaskDelay) {
      return detailData?.statusName as StatusName;
    }

    if (isTaskDelay) {
      if (hasAnyProcessing) {
        return STATUS_NAME.MOT_CONFIRM;
      }

      if (hasDocWithoutFile || hasRejectedFile) {
        return STATUS_NAME.DELAYED;
      }
      return STATUS_NAME.DELAYED;
    }

    return detailData?.statusName as StatusName;
  })();

  const renderPicName = () => {
    if (!detailData || !detailData.projectTaskPics.length) return;
    if (detailData && detailData.projectTaskPics.length === 1) return detailData.projectTaskPics[0].picName;
    const multiplePicName = detailData.projectTaskPics.map((item: any) => {
      if (!item?.picName) return;
      return item?.picStatus ? item.picName : `(${t('account_list:status_account:inactive')}) ${item.picName}`;
    });
    return multiplePicName.join('、');
  };

  const renderExpandIcon = (isActive?: boolean) => {
    return <CaretDownBold type='caret-right' className={`${isActive ? 'rotate-0' : '-rotate-90'}`} />;
  };

  const renderExtraRequiredDocuments = () => (
    <div className='form-view-task-button flex gap-[16px] mr-[5px]'>
      <BaseButton disabled={!isDirty} type='tertiary' onClick={handleCancelUploadFileExternal}>
        {t('button:cancel')}
      </BaseButton>
      <BaseButton disabled={!isDirty || isSubmitting} onClick={handleUploadFileExternal}>
        {t('button:submit')}
      </BaseButton>
    </div>
  );

  const attachmentFilesAvailable = detailData?.projectTaskAttachmentFiles.filter(
    (item: IAttachmentTaskDetail) => item.status === ATTACHMENT_FILE_STATUS.APPROVAL || item.status === ATTACHMENT_FILE_STATUS.DONE
  );
  return (
    <>
      <div className='view-task-detail-project__content'>
        <div className='mb-[12px] px-[20px] py-[12px] bg-white border border-gray2 rounded-[8px]'>
          <div className='flex items-center gap-[63px] justify-between mb-[34px]'>
            <div className='flex gap-[63px] text-lnk min-w-0'>
              <span className='text-18 whitespace-nowrap'>
                {t('project:task_of_project:deadline')}：
                <span className='font-bold text-18'>
                  {DataViewer.displayTime(detailData?.deadline, FORMAT_DATE_EN)}
                  {detailData?.deadlineTime ? `\u3000${dayjs(detailData.deadlineTime, 'HH:mm').format(FORMAT_TIME)}` : ''}
                </span>
              </span>
              <AppTooltip title={renderPicName()}>
                <span className='text-18 truncate'>
                  {t('project:task_of_project:mot_pick:label')}：
                  <span className='truncate font-bold text-18'>{DataViewer.display(renderPicName())}</span>
                </span>
              </AppTooltip>
            </div>
            {renderTaskStatus(displayStatus, '!w-auto shrink-0 px-[8px] py-[2px] !text-base')}
          </div>
          <Collapse
            ghost
            items={[
              {
                key: '1',
                label: <span className='text-[18px] font-semibold leading-[25px] text-lnk'>{t('project:task_of_project:task_description')}</span>,
                children: <FileUploadDetail description={detailData?.detail || ''} />
              }
            ]}
            expandIcon={({ isActive }) => renderExpandIcon(isActive)}
            defaultActiveKey={DEFAULT_ACTIVE_COLLAPSE}
          />
          {!!attachmentFilesAvailable.length && (
            <Collapse
              ghost
              items={[
                {
                  key: '1',
                  label: <span className='text-[18px] font-semibold leading-[25px] text-lnk'>{t('project:task_of_project:attachments')}</span>,
                  children: (
                    <AttachmentFiles
                      attachments={(attachmentFilesAvailable || []) as AttachmentFile[]}
                      showInfo={false}
                      isShowTitle={false}
                      isExternalRole={isExternalRole}
                    />
                  )
                }
              ]}
              expandIcon={({ isActive }) => renderExpandIcon(isActive)}
              defaultActiveKey={DEFAULT_ACTIVE_COLLAPSE}
            />
          )}
        </div>
        {(isExternalRole && !!taskRequireDocuments?.length) || (isInternalRole && taskRequireDocuments) ? (
          <div className='px-[20px] py-[12px] bg-white gap-[12px] border border-gray2 rounded-[8px]'>
            <Collapse
              ghost
              items={[
                {
                  key: '1',
                  label: <span className='text-[18px] font-semibold leading-[25px] text-lnk'>{t('project:task_of_project:required_documents')}</span>,
                  children: <TaskRequiredDocumentsView />,
                  extra: renderExtraRequiredDocuments()
                }
              ]}
              expandIcon={({ isActive }) => renderExpandIcon(isActive)}
              defaultActiveKey={DEFAULT_ACTIVE_COLLAPSE}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
      <ConfirmCancelEditing condition={isDirty && !isSubmitting} />
    </>
  );
};

export default DetailTaskExternal;
